/**
 * stageFit — the largest correctly-shaped box for the remote screen
 * (2026-09-04 owner report).
 *
 * THE DEFECT THIS EXISTS FOR
 * --------------------------
 * The sign-in modal shows a live VM desktop in a cross-origin iframe. Its
 * card carried a `maxHeight` and no `height`, so the card was shrink-to-fit,
 * `flex: 1` on the body had no free space to hand out, and the viewer
 * collapsed to its floor of 240px inside a card up to 1500px wide. The owner
 * reported it as "wider but not larger", and the size toggle — which changed
 * only the width and an inert maxHeight — as "only gets wider".
 *
 * Giving the card a real height is necessary but NOT sufficient, and the
 * halfway fix is worse than it looks: a definite height alone yields roughly
 * a 1452x730 slot at 1920x1080, into which noVNC fits a 1.6 image and leaves
 * ~142px of black pillarbox EACH SIDE. The bars grow in absolute terms as the
 * window grows.
 *
 * WHY A FIXED RATIO IS THE WHOLE ANSWER
 * -------------------------------------
 * The remote framebuffer is immutably 1280x800. Verified end to end: no route
 * accepts a size (browser-service `POST /api/desktop` takes only sessionId,
 * url, userId), Xvfb is spawned with a single `-screen 0 1280x800x24` mode,
 * x11vnc runs without `-xrandr` so there is no SetDesktopSize path to ask for
 * another, and every client requests `resize=scale` — which scales while
 * PRESERVING ASPECT. The remote never learns our box size.
 *
 * So the black bars are a pure function of OUR box's aspect, and the fix is
 * to stop creating them: make the stage exactly 1280:800 and `resize=scale`
 * fills it edge to edge with nothing left to letterbox. That is strictly
 * better than the /desktop page's approach of measuring the bars and painting
 * over them — which is unavailable here anyway, since that page is served BY
 * the VM and is same-origin with the viewer, while our iframe is not.
 */

/**
 * The remote framebuffer.
 *
 * SSOT is browser-service's `DESKTOP_WIDTH` / `DESKTOP_HEIGHT`, fed to Xvfb
 * as the `-screen 0 <W>x<H>x24` mode. Pinned against that file by test: if the
 * fleet ever moves to another geometry, a stale copy here would silently
 * pillarbox this surface again with every other test still green.
 */
export const REMOTE_FRAME = { width: 1280, height: 800 } as const;

/**
 * Never blow the framebuffer up past 1.5x. Beyond that noVNC's scaling is
 * only adding blur, and a stage larger than its own content starts to read as
 * a mistake rather than a choice.
 */
export const MAX_STAGE_SCALE = 1.5;

export interface StageFit {
  width: number;
  height: number;
  /** Displayed size ÷ framebuffer size. Exposed for tests and diagnostics. */
  scale: number;
}

/**
 * The largest box of the frame's aspect that fits the slot.
 *
 * SELF-CONTAINED ON PURPOSE — no free identifiers, no imports, no closure.
 * The Chromium geometry gate ships this function into the page with
 * `fitStage.toString()`, so what it measures is the function production runs
 * rather than a re-implementation that could drift from it. A test pins the
 * absence of free identifiers; keep it that way.
 *
 * Scale-first rather than ratio-first so `maxScale` is a peer term in one
 * `Math.min` instead of a special case afterwards. The height is floored and
 * the width then rounded back off the ratio, which holds aspect error under
 * half a pixel; the final clamp to the slot width kills the one-pixel
 * overshoot that the slot's `overflow: hidden` would otherwise eat.
 */
export function fitStage(
  slotW: number,
  slotH: number,
  frameW: number,
  frameH: number,
  maxScale: number,
): StageFit {
  if (!(slotW > 0) || !(slotH > 0) || !(frameW > 0) || !(frameH > 0)) {
    return { width: 0, height: 0, scale: 0 };
  }
  const scale = Math.min(slotW / frameW, slotH / frameH, maxScale);
  const height = Math.max(1, Math.floor(frameH * scale));
  const width = Math.min(
    Math.floor(slotW),
    Math.max(1, Math.round((height * frameW) / frameH)),
  );
  return { width, height, scale };
}

/** `fitStage` bound to the real framebuffer and the house scale cap. */
export function fitRemoteStage(slotW: number, slotH: number): StageFit {
  return fitStage(slotW, slotH, REMOTE_FRAME.width, REMOTE_FRAME.height, MAX_STAGE_SCALE);
}
